// The stored histogram is keyed by UTC hour (`getUTCHours()` in
// functions/analytics/aggregation.js and incremental.js) because one summary doc
// serves every admin of the event. Local time is therefore a display concern:
// nothing here writes anything back.

const MIN_OFFSET = -12;
const MAX_OFFSET = 14;

/** Every whole-hour UTC offset an operator can pick, west to east. */
export const OFFSET_HOURS = Array.from(
  { length: MAX_OFFSET - MIN_OFFSET + 1 },
  (_, i) => MIN_OFFSET + i
);

function clamp(hours) {
  return Math.min(MAX_OFFSET, Math.max(MIN_OFFSET, hours));
}

/**
 * Re-key a `{utcHour: count}` histogram into local hours, in clock order.
 * Buckets that cross midnight wrap around rather than falling off the ends.
 */
export function shiftHistogram(byHourOfDay, offsetHours) {
  if (!byHourOfDay) return [];
  const shift = Number.isFinite(offsetHours) ? offsetHours : 0;
  const counts = new Map();
  for (const [key, count] of Object.entries(byHourOfDay)) {
    const utcHour = Number(key);
    if (!Number.isInteger(utcHour) || utcHour < 0 || utcHour > 23) continue;
    if (typeof count !== "number" || count <= 0) continue;
    const hour = (((utcHour + shift) % 24) + 24) % 24;
    counts.set(hour, (counts.get(hour) || 0) + count);
  }
  return [...counts.entries()]
    .map(([hour, count]) => ({ hour, count }))
    .sort((a, b) => a.hour - b.hour);
}

/**
 * The browser's own UTC offset, in whole hours, east positive. Half-hour zones
 * (India +5:30, Nepal +5:45) round to the nearest hour — the buckets themselves
 * are whole hours, so there is nothing finer to show.
 */
export function detectOffsetHours() {
  const minutes = new Date().getTimezoneOffset();
  if (!Number.isFinite(minutes)) return 0;
  return clamp(Math.round(-minutes / 60));
}

/** `UTC+02`, `UTC-05` — the label on the dropdown and its options. */
export function formatUtcOffset(offsetHours) {
  const sign = offsetHours < 0 ? "-" : "+";
  return `UTC${sign}${String(Math.abs(offsetHours)).padStart(2, "0")}`;
}
